/** Serverzustand der Datenschutzwerkzeuge -- F5-11 bis F5-15. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  createRequest,
  downloadSubjectAccess,
  listPersonalData,
  listRequests,
  listRetentionJobs,
  runRetention,
  save,
  updateRequest,
} from './api';

export const privacyKeys = {
  all: ['privacy'] as const,
  requests: (openOnly: boolean) => ['privacy', 'requests', openOnly] as const,
  retentionJobs: ['privacy', 'retention-jobs'] as const,
  personalData: ['privacy', 'personal-data'] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function useDataSubjectRequests(openOnly: boolean) {
  return useQuery({
    queryKey: privacyKeys.requests(openOnly),
    queryFn: () => listRequests(openOnly),
    retry: retryUnlessPermanent,
  });
}

export function useCreateDataSubjectRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createRequest(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['privacy', 'requests'] });
    },
  });
}

export function useUpdateDataSubjectRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { requestId: string; payload: Record<string, unknown> }) =>
      updateRequest(variables.requestId, variables.payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['privacy', 'requests'] });
    },
  });
}

export function useRetentionJobs() {
  return useQuery({
    queryKey: privacyKeys.retentionJobs,
    queryFn: listRetentionJobs,
    retry: retryUnlessPermanent,
  });
}

export function useRunRetention() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dryRun: boolean) => runRetention(dryRun),
    onSuccess: async () => {
      // Ein scharfer Lauf hat Vorgaenge entfernt -- was sonst noch im
      // Zwischenspeicher liegt, zeigt sonst Zeilen, die es nicht mehr gibt.
      await queryClient.invalidateQueries();
    },
  });
}

/** Das Verzeichnis aendert sich nur mit einer neuen Fassung der Anwendung. */
export function usePersonalDataRegister() {
  return useQuery({
    queryKey: privacyKeys.personalData,
    queryFn: listPersonalData,
    staleTime: 60 * 60 * 1000,
    retry: retryUnlessPermanent,
  });
}

/**
 * Holt die Auskunft nach Art. 15 als PDF und legt sie ab -- F5-11.
 *
 * Als Mutation, damit ein gescheiterter Abruf zu sehen ist und der Knopf
 * nicht einfach nichts tut.
 */
export function useSubjectAccessExport() {
  return useMutation({
    mutationFn: async ({ contactId, filename }: { contactId: string; filename: string }) => {
      save(await downloadSubjectAccess(contactId, 'pdf'), filename);
    },
  });
}
